import { RouteV2Id } from './routesConfigV2';

export type PageNode = {
  [K in RouteV2Id]?: PageNode | RouteV2Id | null;
};

/**
 * DEVELOPER NOTE:
 *  ---------------
 *  Here we compose our sitemap. It is essentially a graph that represents all the
 *  supported flows in the app. It is used to find the path to a page by pages using breadcrumbs.
 *  And also helps us to keep track of what is going on in the app.
 *
 *  The sitemap is a tree where keys are the page ids (taken from the routeConfigV2), their values
 *  are either other pages or null (when the page has no further redirects deeper in the flow).
 *  ---------------
 */

export const SITEMAP: PageNode = Object.freeze({
  // Broker
  [RouteV2Id.INITIATE_GROUP]: null,
  [RouteV2Id.ZYWAVE_GROUP_INFO]: null,
  [RouteV2Id.ACCOUNT_SETTINGS]: null,
  [RouteV2Id.NOTIFICATION_SETTINGS]: null,
  [RouteV2Id.GROUP_HUB]: {
    [RouteV2Id.INITIATE_PROPOSAL]: null,
    [RouteV2Id.QUOTE_CHECKLIST]: {
      [RouteV2Id.QUOTE_PROPOSAL_INFO]: null,
      [RouteV2Id.QUOTE_ADD_EMPLOYEES]: {
        [RouteV2Id.QUOTE_ADD_EMPLOYEES_CENSUS_UPLOAD]: {
          [RouteV2Id.QUOTE_ADD_EMPLOYEES_CENSUS_REVIEW]: null,
        },
        [RouteV2Id.QUOTE_ADD_EMPLOYEES_FULL_FORM]: null,
      },
      [RouteV2Id.IMQ_ADD_EMPLOYEES]: {
        [RouteV2Id.IMQ_ADD_EMPLOYEES_CENSUS_UPLOAD]: {
          [RouteV2Id.IMQ_ADD_EMPLOYEES_CENSUS_REVIEW]: null,
        },
        [RouteV2Id.IMQ_ADD_EMPLOYEES_FULL_FORM]: null,
        [RouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE_ENTRY]: null,
        [RouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE]: {
          [RouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE_MEDICAL_CATEGORY]: null,
        },
        [RouteV2Id.IMQ_ADD_EMPLOYEES_EMPLOYMENT_DETAILS]: null,
      },
      [RouteV2Id.DOWNLOAD_IMQS]: null,
      [RouteV2Id.INITIATE_SHELF_ADD_QUOTE]: {
        [RouteV2Id.QUOTE_OVERRIDE_RATES]: {
          [RouteV2Id.QUOTE_OVERRIDE_RATES_PLAN]: null,
        },
        [RouteV2Id.QUOTE_ESTIMATED_COST]: null,
      },
      [RouteV2Id.INITIATE_SHELF_BROWSING]: null,
      [RouteV2Id.ZYWAVE_QUOTE_REDIRECT]: null,
    },
    [RouteV2Id.ENROLLMENT_CHECKLIST]: {
      [RouteV2Id.EMPLOYER_FORMS]: null,
    },
  },

  // Employee + Employer
  [RouteV2Id.EMPLOYER_PROFILE]: null,
  [RouteV2Id.EMPLOYEE_PROFILE]: {
    [RouteV2Id.SUBSCRIBER_HEALTH_QUESTIONNAIRE_ENTRY]: null,
    [RouteV2Id.SUBSCRIBER_HEALTH_QUESTIONNAIRE]: {
      [RouteV2Id.SUBSCRIBER_HEALTH_QUESTIONNAIRE_MEDICAL_CATEGORY]: null,
    },
    [RouteV2Id.SUBSCRIBER_EMPLOYMENT_DETAILS]: null,
    [RouteV2Id.SUBSCRIBER_IMQ_SUBSCRIBER_PROFILE]: null,
    [RouteV2Id.SUBSCRIBER_IMQ_SUBSCRIBER_DEPENDENTS]: null,
  },

  // Tools
  [RouteV2Id.TRIGGER_RENEWALS]: {
    [RouteV2Id.UPLOAD_ALL_EXHIBITS]: null,
  },
});

/**
 * A depth-first search algorithm to find the path to a page in the sitemap.
 *  Called on the initial render of every page in the app.
 */
export function findPathToPage(pageId: RouteV2Id): RouteV2Id[] {
  function helper(current: PageNode, path: RouteV2Id[]): RouteV2Id[] | null {
    // eslint-disable-next-line no-restricted-syntax
    for (const key of Object.keys(current) as RouteV2Id[]) {
      if (key === pageId) {
        return [...path, key]; // Found it
      }
      const child = current[key];
      if (child) {
        const result = helper(child as PageNode, [...path, key]);
        if (result) return result; // bubble up the found path
      }
    }
    return null; // not found in this branch
  }

  const path = helper(SITEMAP, []);
  if (!path) {
    throw new Error(`RouteV2Id "${pageId}" does not exist in the sitemap.`);
  }
  return path;
}
